import type { MediaAssetCategory } from '@/types/admin'
import { runtimeEnv } from '@/utils/runtimeEnv'
import ImageIcon from '@mui/icons-material/Image'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DescriptionIcon from '@mui/icons-material/Description'
import FolderIcon from '@mui/icons-material/Folder'
import type { SvgIconComponent } from '@mui/icons-material'

/**
 * Format file size in bytes to human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

/**
 * Get icon component for a MIME type
 */
export function getFileIcon(mimeType: string): SvgIconComponent {
  if (mimeType.startsWith('image/')) return ImageIcon
  if (mimeType === 'application/pdf') return PictureAsPdfIcon
  return DescriptionIcon
}

/**
 * Get icon component for a media category
 */
export function getCategoryIcon(category: MediaAssetCategory): SvgIconComponent {
  switch (category) {
    case 'Images':
      return ImageIcon
    case 'Documents':
      return DescriptionIcon
    case 'Guidelines':
    case 'ReferenceMaterials':
      return FolderIcon
    default:
      return DescriptionIcon
  }
}

/**
 * Check if a MIME type is an image
 */
export function isImageType(mimeType: string): boolean {
  return mimeType.startsWith('image/')
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * HTML that embeds an asset in content: an <img> for images, a link otherwise.
 * Uses the relative /api/media/ URL, which the viewer rewrites to the API base URL.
 */
export function buildMediaHtmlTag(asset: { systemFileName: string; mimeType: string; displayName: string; altText?: string | null }): string {
  const relativeUrl = `/api/media/${asset.systemFileName}`
  return isImageType(asset.mimeType)
    ? `<img src="${relativeUrl}" alt="${escapeHtml(asset.altText || asset.displayName)}" />`
    : `<a href="${relativeUrl}">${escapeHtml(asset.displayName)}</a>`
}

/**
 * Get thumbnail URL for an asset
 */
export function getAssetThumbnailUrl(systemFileName: string): string {
  const baseUrl = runtimeEnv.VITE_API_BASE_URL || ''
  return `${baseUrl}/api/media/${systemFileName}?inline=true`
}

/**
 * Get download URL for an asset
 */
export function getAssetDownloadUrl(systemFileName: string): string {
  const baseUrl = runtimeEnv.VITE_API_BASE_URL || ''
  return `${baseUrl}/api/media/${systemFileName}`
}

/**
 * Format category name for display
 */
export function formatCategoryName(category: string): string {
  // Convert "ReferenceMaterials" to "Reference Materials"
  return category.replace(/([A-Z])/g, ' $1').trim()
}

/**
 * Validate file for upload
 */
export function validateFile(
  file: File,
  maxSizeBytes: number = 52428800, // 50MB default
  allowedTypes: string[] = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'application/rtf',
  ]
): { valid: boolean; error?: string } {
  if (!file) {
    return { valid: false, error: 'No file selected' }
  }

  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds maximum of ${formatFileSize(maxSizeBytes)}`,
    }
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `File type '${file.type}' is not allowed`,
    }
  }

  return { valid: true }
}
